import { type FC, type ReactNode } from "react";

import { cx } from "@emotion/css";
import { Button, Control, Status, Tag, TagContainer, TagType } from "@snapchat/snap-design-system";
import { Field, Form, Formik } from "formik";
import { type ObjectSchema } from "yup";

import { Flexbox } from "../../components";
import { fieldErrorCreator } from "../fieldErrorCreator";
import { FormikInput } from "../formikComponentsSds";

import { buttonWithError, fullSizeInputStyle, fullWidthStyle, tagContainerSpacingBottom } from "./TagsInput.styles";

interface TagsInputProps {
    items: readonly string[];
    onAddItem?: (name: string) => void;
    onRemoveItem?: (name: string) => void;
    disabled?: boolean;
    controlStyle?: string;
    label?: ReactNode;
    tooltip?: ReactNode;
    buttonCta?: ReactNode;
    validationSchema?: ObjectSchema<{
        item?: string;
    }>;
    initialValues?: {
        item: string;
    };
    readOnly?: boolean;
}

export const TagsInput: FC<TagsInputProps> = ({
    items,
    onAddItem = () => {
        // no-op
    },
    onRemoveItem = () => {
        // no-op
    },
    disabled,
    controlStyle = "",
    label,
    tooltip,
    buttonCta = "Add",
    readOnly = false,
    initialValues = {
        item: "",
    },
    validationSchema,
}) => {
    return (
        <Control className={controlStyle} label={label} tooltip={tooltip}>
            <Flexbox flexDirection="column" gap="12px" className={cx(fullWidthStyle, tagContainerSpacingBottom)}>
                {readOnly ? null : (
                    <Formik
                        onSubmit={(values, actions) => {
                            onAddItem(values.item);

                            // Reset the form
                            actions.resetForm();
                        }}
                        initialValues={initialValues}
                        validationSchema={validationSchema}
                        validateOnMount={true}
                    >
                        {({ errors, touched, handleSubmit, setTouched }) => {
                            const itemError = fieldErrorCreator(errors, touched)("item");
                            return (
                                <Form>
                                    <Flexbox
                                        onBlur={() => {
                                            // Reset the touched state, so that the error message is not shown
                                            void setTouched({ item: false });
                                        }}
                                        gap="12px"
                                        className={fullWidthStyle}
                                        tabIndex={0}
                                        role="button"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        onKeyDown={(event) => {
                                            if (event.key === "Enter" || event.key === " ") {
                                                const error = errors.item;
                                                if (error) {
                                                    void setTouched({ item: true });
                                                } else {
                                                    handleSubmit();
                                                }
                                                event.preventDefault(); // To prevent default scrolling on space
                                            }
                                        }}
                                    >
                                        <Field
                                            id="item"
                                            name="item"
                                            component={FormikInput}
                                            className={fullSizeInputStyle}
                                            status={itemError}
                                            statusType={itemError && Status.ERROR}
                                            disabled={disabled}
                                        />

                                        <Button
                                            className={cx(itemError && buttonWithError)}
                                            isDisabled={Boolean(errors.item) || disabled}
                                            onClick={() => {
                                                handleSubmit();
                                            }}
                                        >
                                            {buttonCta}
                                        </Button>
                                    </Flexbox>
                                </Form>
                            );
                        }}
                    </Formik>
                )}
                <TagContainer>
                    {items.map((item) => (
                        <Tag
                            type={TagType.SUBTLE}
                            key={item}
                            onClose={() => {
                                if (!readOnly) {
                                    onRemoveItem(item);
                                }
                            }}
                        >
                            {item}
                        </Tag>
                    ))}
                </TagContainer>
            </Flexbox>
        </Control>
    );
};
